import { useParams } from "react-router-dom";
import { Box, Divider } from "@mui/material";

import { useUserProfile } from "../../../../User/api/queries/useUserProfile";

import { useChatThread } from "../../../hooks/useChatThread";
import { useScrollToBottom } from "../../../hooks/useScrollToBottom";

import MessagesList from "./MessagesList";
import ChatInputField from "./ChatInputField";
import ChatContainerHeader from "./ChatContainerHeader";
import DefaultChatContainer from "./DefaultChatContainer";

// Updated ChatContainer with mobile support
interface ChatContainerProps {
  isLoading: boolean;
  onBackToSidebar?: () => void;
  isMobile?: boolean;
  showBackButton?: boolean;
}

const ChatContainer = ({ isLoading, onBackToSidebar, isMobile, showBackButton }: ChatContainerProps) => {
  const { chatId, sendToID } = useParams();

  if (!chatId || !sendToID) return <DefaultChatContainer />;
  return (
    <ActiveChatContainer
      sendToID={sendToID}
      isChatLoading={isLoading}
      onBackToSidebar={onBackToSidebar}
      isMobile={isMobile}
      showBackButton={showBackButton}
    />
  );
};

const ActiveChatContainer = ({
  sendToID,
  isChatLoading,
  onBackToSidebar,
  isMobile,
  showBackButton,
}: {
  sendToID: string;
  isChatLoading: boolean;
  onBackToSidebar?: () => void;
  isMobile?: boolean;
  showBackButton?: boolean;
}) => {
  const { data: senderData, isLoading: isSenderDataLoading } = useUserProfile(String(sendToID));
  const { messages, isMessagesLoading, handleSendTextMessage, handleOnKeyDownWithinChatInput, chatInputRef } =
    useChatThread();

  const { elementRef, scrollToBottom } = useScrollToBottom(messages);

  return (
    <Box sx={styles.container}>
      <ChatContainerHeader
        senderUserData={senderData}
        isChatLoading={isChatLoading || isSenderDataLoading}
        onBackToSidebar={onBackToSidebar}
        showBackButton={showBackButton}
      />
      <Divider flexItem />
      <MessagesList
        senderUserData={senderData}
        isSenderDataLoading={isSenderDataLoading}
        messages={messages}
        isMessagesLoading={isMessagesLoading}
        ref={elementRef}
      />
      <ChatInputField
        onSendMessage={handleSendTextMessage}
        onKeyDown={handleOnKeyDownWithinChatInput}
        scrollToBottomOfChatContainer={scrollToBottom}
        chatInputRef={chatInputRef}
      />
    </Box>
  );
};

const styles = {
  container: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: {
      xs: "60vh", // Take at least 60% of viewport height on mobile
      md: "auto", // Auto height on desktop
    },
    backgroundColor: {
      xs: "#FFFFFF", // White background on mobile for better contrast
      md: "transparent", // Transparent on desktop
    },
    borderRadius: {
      xs: "0 0 8px 8px", // Rounded bottom corners on mobile
      md: "0", // No border radius on desktop
    },
    // Add subtle border on mobile for better separation
    borderTop: {
      xs: "1px solid #EAECF0", // Top border on mobile
      md: "none", // No border on desktop
    },
    position: "relative", // Ensure proper positioning
    zIndex: {
      xs: 0, // Stay below sidebar on mobile
      md: "auto", // Auto z-index on desktop
    },
  },
};

export default ChatContainer;
